import { Injectable } from '@angular/core';
import { FormRule, FormRuleFactory, RuleAction } from 'src/app/shared/forms';
import { TokenClaimsService } from 'src/app/shared/services';
import { SubmissionConstants } from '../models/constants/submissions-constants';
import { FormGroup } from '@angular/forms';
import { NeemV3CustomRules } from './custom-rules/neemV3Rules';
import { SbraV3CustomRules } from './custom-rules/sbraV3Rules';
import { CustomerInfoRules } from './custom-rules/customerRules';
import { ManufactureDateRules } from './custom-rules/manufactureDateRules';
import { SbraV2CustomRules } from './custom-rules/sbraV2Rules';
import { HeatPumpDocumentRules } from './custom-rules/heatPumpDocumentRules';
import { WaterHeaterRules } from './custom-rules/waterHeaterRules';

@Injectable({ providedIn: 'root' })
export class CustomFormRulesService {
  constructor(
    private tokenService: TokenClaimsService,
    private ruleFact: FormRuleFactory,
    private submissionsConst: SubmissionConstants,
  ) {}

  getCustomRules(
    formStructureId: string,
    formValues: FormGroup,
    savedWaterHeatingValue?: string,
  ): FormRule[] {
    switch (formStructureId.toString()) {
      case '2':
        return [
          ...new SbraV2CustomRules(
            this.ruleFact,
            this.submissionsConst,
          ).getCustomRules(formValues),
          ...this.getCustomVersionTwoAdminRules(formValues),
        ];
      case '1':
        return [
          ...new SbraV3CustomRules(
            this.ruleFact,
            this.submissionsConst,
          ).getCustomRules(formValues),
          ...this.getCustomVersionThreeRules(formValues, savedWaterHeatingValue),
          ...this.getCustomVersionThreeAdminRules(formValues),
        ];
      case '4':
        return [
          ...this.getCustomNeemVersionTwoRules(formValues),
          ...this.getCustomNeemVersionTwoAdminRules(formValues),
        ];
      case '3':
        return [
          ...new NeemV3CustomRules(
            this.ruleFact,
            this.submissionsConst,
          ).getCustomRules(formValues),
          ...this.getCustomNeemVersionThreeAdminRules(formValues),
        ];
      default:
        return [];
    }
  }
  private getCustomVersionTwoAdminRules(subValues: FormGroup): FormRule[] {
    return [];
  }

  private getCustomVersionThreeRules(
    subValues: FormGroup,
    savedWaterHeatingValue?: string,
  ): FormRule[] {
    const homeCenterType = subValues.get('homeCenterType')?.value;
    const externalDuctLocation = subValues.get('externalDuctLocation').value;
    const hasExternalDucts = subValues.get('hasExternalDucts').value;
    const status = subValues.get('status').value;
    return [
      ...new CustomerInfoRules(
        this.ruleFact,
        this.submissionsConst,
      ).getCustomRules(subValues),
      this.ruleFact.createRule(`'${homeCenterType}' === '1'`, [
        {
          action: RuleAction.MAKE_VISIBLE,
          affectingType: 'control',
          applyTo: 'noInvoiceAvailable',
        },
      ]),
      ...new ManufactureDateRules(
        this.ruleFact,
        this.submissionsConst,
      ).getCustomRules(subValues),
      ...new WaterHeaterRules(
        this.ruleFact,
        this.submissionsConst,
        this.tokenService,
      ).getV3CustomRules(subValues, savedWaterHeatingValue),
      this.ruleFact.createRule(
        `'{primarySpaceHeatingCooling}' === 'Omit Furnace'`,
        [
          {
            action: RuleAction.MAKE_HIDDEN,
            affectingType: 'group',
            applyTo: 'Heat Pump',
          },
        ],
      ),
      this.ruleFact.createRule(
        `${!hasExternalDucts} && ${!externalDuctLocation}`,
        [
          {
            action: RuleAction.MAKE_INVALID,
            affectingType: 'control',
            applyTo: 'externalDuctLocation',
          },
        ],
      ),
      this.ruleFact.createRule(
        `${status === 'Ready To Submit'} && ${hasExternalDucts}`,
        [
          {
            action: RuleAction.SET_VALUE,
            affectingType: 'control',
            applyTo: 'externalDuctLocation',
            value: '',
          },
        ],
      ),
    ];
  }

  private getCustomVersionThreeAdminRules(subValues: FormGroup): FormRule[] {
    if (!this.tokenService.isEnergyCreditAdmin) {
      return [];
    }

    const energyEfficiencyType = subValues.get('energyEfficiencyType')?.value;

    return [
      this.ruleFact.createRule(`${true}`, [
        {
          action: RuleAction.MAKE_ENABLED,
          affectingType: 'control',
          applyTo: 'isEBuilt',
        },
      ]),
      this.ruleFact.createRule(
        `'${energyEfficiencyType}' === 'none' || !'${energyEfficiencyType}'`,
        [
          {
            action: RuleAction.MAKE_HIDDEN,
            affectingType: 'control',
            applyTo: 'energyEfficiencyTypeBanner',
          },
        ],
      )
    ];
  }

  private getCustomNeemVersionTwoRules(subValues: FormGroup): FormRule[] {
    const designPackage = subValues.get('designPackage').value;
    const status = subValues.get('status').value;
    const shouldMarkDesignPackageRequired =
      (status === this.submissionsConst.statusReadyToSubmit ||
        status === this.submissionsConst.statusSubmittedManually) &&
      !designPackage;
    return [
      ...new CustomerInfoRules(
        this.ruleFact,
        this.submissionsConst,
      ).getCustomRules(subValues),
      ...new ManufactureDateRules(
        this.ruleFact,
        this.submissionsConst,
      ).getCustomRules(subValues),
      ...new HeatPumpDocumentRules(
        this.ruleFact,
        this.submissionsConst,
      ).getV2CustomRules(subValues),
      this.ruleFact.createRule(`(${!designPackage})`, [
        {
          action: RuleAction.MAKE_HIDDEN,
          affectingType: 'group',
          applyTo: 'Heat Pump Information',
        },
      ]),
      this.ruleFact.createRule(`${shouldMarkDesignPackageRequired}`, [
        {
          action: RuleAction.MAKE_INVALID,
          affectingType: 'control',
          applyTo: 'designPackage',
        },
      ]),
      this.ruleFact.createRule(`${!shouldMarkDesignPackageRequired}`, [
        {
          action: RuleAction.CLEAR_ERROR_MESSAGE,
          affectingType: 'control',
          applyTo: 'designPackage',
        },
      ]),
    ];
  }
  private getCustomNeemVersionTwoAdminRules(subValues: FormGroup): FormRule[] {
    return [];
  }

  private getCustomNeemVersionThreeAdminRules(
    subValues: FormGroup,
  ): FormRule[] {
    if (!this.tokenService.isEnergyCreditAdmin) {
      return [];
    }
    const energyEfficiencyType = subValues.get('energyEfficiencyType')?.value;
    return [
      this.ruleFact.createRule(`${true}`, [
        {
          action: RuleAction.MAKE_ENABLED,
          affectingType: 'control',
          applyTo: 'isEBuilt',
        },
      ]),
      this.ruleFact.createRule(
        `'${energyEfficiencyType}' === 'none' || !'${energyEfficiencyType}'`,
        [
          {
            action: RuleAction.MAKE_HIDDEN,
            affectingType: 'control',
            applyTo: 'energyEfficiencyTypeBanner',
          },
        ],
      ),
    ];
  }
}
